import React, { useEffect, useState } from "react";
import api from "../services/api";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import { useAuth } from "../utils/auth";
import { useNavigate } from "react-router-dom";

const EditProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("Very Weak");
  const [profilePic, setProfilePic] = useState(null);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/login");
      } 
    }
  }, [user, loading, navigate]);

  const checkPasswordStrength = (value) => {
    setPassword(value);
    const hasLetters = /[a-zA-Z]/.test(value);
    const hasNumbers = /[0-9]/.test(value);
    const hasSpecial = /[^a-zA-Z0-9]/.test(value);
    const isLong = value.length >= 8;

    if (hasLetters && hasNumbers && hasSpecial && isLong) {
      setPasswordStrength("Strong");
    } else if ((hasLetters && hasNumbers) || (hasLetters && hasSpecial) || (hasNumbers && hasSpecial)) {
      setPasswordStrength("Medium");
    } else if (value.length >= 6) {
      setPasswordStrength("Weak");
    } else {
      setPasswordStrength("Very Weak");
    }
  };

  const strengthColors = {
    "Very Weak": "bg-red-500 w-1/4",
    Weak: "bg-orange-400 w-2/4",
    Medium: "bg-yellow-400 w-3/4",
    Strong: "bg-green-500 w-full",
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get("/api/profile", { withCredentials: true });
        setProfile({ ...response.data, password: "" }); // include password field
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Unable to load profile. Please login again.");
      }
    };
    fetchProfile();
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    // Optional: Validate profilePic size and type
    if (profilePic && profilePic.size > 1024 * 1024) {
      alert("Image must be under 1MB");
      return;
    }

    const formData = new FormData();
    formData.append("first_name", profile.first_name);
    formData.append("last_name", profile.last_name);
    formData.append("email", profile.email);
    formData.append("password", password);
    formData.append("phone_number", profile.phone_number || ""); 
    if (profilePic) formData.append("profile_image", profilePic);

    try {
      await api.post("/api/update-profile", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Profile updated successfully");
    } catch (err) {
      console.error("Update failed", err);
      alert("Update failed. Please try again.");
    }
  };

  if (error) return <div className="text-red-500 text-center p-6">{error}</div>;
  if (!profile) return <div className="text-center p-6">Loading profile...</div>;

  return (
    <>
      <Helmet>
        <title>Edit Profile - Art India Education</title>
      </Helmet>
      <Navbar />

      {/* Top Banner */}
      <section className="relative h-[50vh] w-full overflow-hidden">
        <div className="relative w-full h-full overflow-hidden">
          <div className="absolute inset-0 bg-black/50 z-10"></div>
          <img src="/edit-profile-page-banner-background.jpg" alt="Banner" className="absolute top-[-50%] h-[150%] w-full object-cover z-0" />
          <h1 className="absolute inset-0 flex items-center justify-center text-white text-6xl font-extrabold z-20">
            Edit Profile
          </h1>
        </div>
      </section>

      {/* Form Section */}
      <section className="px-8 py-12 max-w-xl mx-auto font-['EB_Garamond','Nunito','sans-serif']">
        <form onSubmit={handleProfileUpdate} className="space-y-6">
          {/* User ID (read-only) */}
          <div>
            <label className="block text-sm font-semibold">User ID</label>
            <input
              value={profile.user_id || ""}
              disabled
              className="w-full border-b border-gray-400 bg-transparent p-1 text-gray-800"
            />
          </div>

          {/* First Name */}
          <div>
            <label className="block text-sm font-semibold">* First Name</label>
            <input
              value={profile.first_name}
              onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
              className="w-full border-b border-gray-400 bg-transparent p-1"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-semibold">* Last Name</label>
            <input
              value={profile.last_name}
              onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
              className="w-full border-b border-gray-400 bg-transparent p-1"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold">* Email Address</label>
            <input
              type="email"
              value={profile.email}
              disabled
              className="w-full border-b border-gray-400 bg-transparent p-1"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold">* Password</label>
            <input
              type="password"
              // value={password}
              onChange={(e) => checkPasswordStrength(e.target.value)}
              className="w-full border-b border-gray-400 bg-transparent p-1"
              placeholder="Create New Password"
            />
            <div className="mt-1 h-2 w-full bg-gray-200 rounded">
              <div className={`h-2 rounded ${strengthColors[passwordStrength]}`}></div>
            </div>
            <p className="text-sm mt-1 text-gray-600">Strength: {passwordStrength}</p>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold">Phone Number</label>
            <input
              type="tel"
              value={profile.phone_number || ""}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              className="w-full border-b border-gray-400 bg-transparent p-1"
            />
          </div>

          {/* Profile Picture */}
          <div>
            <label className="block text-sm font-semibold">Profile Picture</label>
            <input
              type="file"
              accept="image/png, image/jpeg"
              onChange={(e) => setProfilePic(e.target.files[0])}
              className="w-full text-sm mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Max 1MB, JPEG or PNG format</p>
          </div>

          {/* Submit */}
          <div className="text-center pt-4">
            <button type="submit" className="bg-[rgb(234,126,93)] text-white px-6 py-2 font-semibold rounded shadow hover:bg-opacity-90">
              Update Profile
            </button>
          </div>
        </form>
      </section>
    </>
  );
};

export default EditProfilePage;